// pages/admin/AdminInheritances.js
import React, { useState, useEffect, useCallback } from 'react';
import { useAdmin } from '../../contexts/AdminContext';
import AdminConfirmDialog from './components/AdminConfirmDialog';
import AdminPagination from './components/AdminPagination';

const AdminInheritances = () => {
  const { apiCall, addNotification } = useAdmin();
  const [inheritances, setInheritances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalInheritances, setTotalInheritances] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [confirmDialog, setConfirmDialog] = useState({ show: false, inheritance: null, action: '' });

  const limit = 10;

  const debounce = (func, wait) => {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  };

  const debouncedSearch = useCallback(
    debounce((term) => {
      setCurrentPage(1);
      loadInheritances(1, term, statusFilter, typeFilter);
    }, 500),
    [statusFilter, typeFilter]
  );

  useEffect(() => {
    loadInheritances();
  }, [currentPage, statusFilter, typeFilter]);

  useEffect(() => {
    debouncedSearch(searchTerm);
  }, [searchTerm, debouncedSearch]);

  const loadInheritances = async (page = currentPage, search = searchTerm, status = statusFilter, type = typeFilter) => {
    try {
      setLoading(true);
      
      const queryParams = new URLSearchParams({
        page,
        limit,
        ...(search && { search }),
        ...(status && { status }),
        ...(type && { type })
      });

      const response = await apiCall(
        `https://phenixloc-api.fnvt.net/api/admin/inheritances?${queryParams.toString()}`
      );

      setInheritances(response.inheritances);
      setCurrentPage(response.pagination.page);
      setTotalPages(response.pagination.totalPages);
      setTotalInheritances(response.pagination.total);

    } catch (error) {
      console.error('Error loading inheritances:', error);
      addNotification('Error loading inheritances', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleInheritanceAction = (inheritance, action) => {
    setConfirmDialog({
      show: true,
      inheritance,
      action,
      title: getActionTitle(action),
      message: getActionMessage(inheritance, action),
      confirmText: getActionButtonText(action),
      confirmClass: getActionButtonClass(action)
    });
  };

  const confirmInheritanceAction = async () => {
    const { inheritance, action } = confirmDialog;
    
    try {
      await apiCall('https://phenixloc-api.fnvt.net/api/admin/inheritances/action', {
        method: 'POST',
        body: JSON.stringify({
          inheritanceId: inheritance.id,
          action
        })
      });

      const actionMessages = {
        'activate': 'Inheritance activated successfully.',
        'deactivate': 'Inheritance deactivated successfully.',
        'force-deliver': 'Inheritance delivered successfully.',
        'delete': 'Inheritance deleted successfully.'
      };

      addNotification(actionMessages[action] || 'Action completed successfully.', 'success');
      loadInheritances();
      setConfirmDialog({ show: false, inheritance: null, action: '' });

    } catch (error) {
      console.error(`Error performing inheritance action (${action}):`, error);
      addNotification(`Error: ${error.message}`, 'error');
    }
  };

  const getActionTitle = (action) => {
    const titles = {
      'activate': 'Activate Inheritance',
      'deactivate': 'Deactivate Inheritance',
      'force-deliver': 'Force Deliver Inheritance',
      'delete': 'Delete Inheritance'
    };
    return titles[action] || 'Confirm Action';
  };

  const getActionMessage = (inheritance, action) => {
    const messages = {
      'activate': `Are you sure you want to activate inheritance "${inheritance.filename}"?`,
      'deactivate': `Are you sure you want to deactivate inheritance "${inheritance.filename}"? It will not be deliverable while inactive.`,
      'force-deliver': `Are you sure you want to force deliver inheritance "${inheritance.filename}" to ${inheritance.recipientEmail}? This action cannot be undone.`,
      'delete': `Are you sure you want to delete inheritance "${inheritance.filename}"? This action cannot be undone.`
    };
    return messages[action] || 'Are you sure you want to proceed?';
  };

  const getActionButtonText = (action) => {
    const texts = {
      'activate': 'Activate',
      'deactivate': 'Deactivate',
      'force-deliver': 'Force Deliver',
      'delete': 'Delete'
    };
    return texts[action] || 'Confirm';
  };

  const getActionButtonClass = (action) => {
    const classes = {
      'activate': 'admin-btn-success',
      'deactivate': 'admin-btn-warning',
      'force-deliver': 'admin-btn-primary',
      'delete': 'admin-btn-danger'
    };
    return classes[action] || 'admin-btn-primary';
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return <span className="admin-badge admin-badge-active">Active</span>;
      case 'inactive':
        return <span className="admin-badge admin-badge-warning">Inactive</span>;
      case 'delivered':
        return <span className="admin-badge admin-badge-success">Delivered</span>;
      default:
        return <span className="admin-badge admin-badge-secondary">Unknown</span>;
    }
  };

  const getTypeBadge = (type) => {
    const badges = {
      'file': <span className="admin-badge admin-badge-file">File</span>,
      'password': <span className="admin-badge admin-badge-password">Password</span>,
      'phrase': <span className="admin-badge admin-badge-phrase">Phrase</span>,
      'banking': <span className="admin-badge admin-badge-banking">Banking</span>
    };
    return badges[type] || <span className="admin-badge admin-badge-file">File</span>;
  };

  const getActionButtons = (inheritance) => {
    const buttons = [];
    
    // Always show view button (placeholder for future implementation)
    buttons.push(
      <button 
        key="view"
        className="admin-btn-icon admin-btn-view" 
        title="View Details"
        onClick={() => addNotification('Inheritance details view not yet implemented', 'info')}
      >
        <i className="fas fa-eye"></i>
      </button>
    );

    switch (inheritance.status) {
      case 'active':
        buttons.push(
          <button 
            key="deliver"
            className="admin-btn-icon admin-btn-primary" 
            title="Force Deliver"
            onClick={() => handleInheritanceAction(inheritance, 'force-deliver')}
          >
            <i className="fas fa-paper-plane"></i>
          </button>,
          <button 
            key="deactivate"
            className="admin-btn-icon admin-btn-warning" 
            title="Deactivate"
            onClick={() => handleInheritanceAction(inheritance, 'deactivate')}
          >
            <i className="fas fa-ban"></i>
          </button>,
          <button 
            key="delete"
            className="admin-btn-icon admin-btn-danger" 
            title="Delete"
            onClick={() => handleInheritanceAction(inheritance, 'delete')}
          >
            <i className="fas fa-trash"></i>
          </button>
        );
        break;
      case 'inactive':
        buttons.push(
          <button 
            key="activate"
            className="admin-btn-icon admin-btn-success" 
            title="Activate"
            onClick={() => handleInheritanceAction(inheritance, 'activate')}
          >
            <i className="fas fa-check-circle"></i>
          </button>,
          <button 
            key="delete"
            className="admin-btn-icon admin-btn-danger" 
            title="Delete"
            onClick={() => handleInheritanceAction(inheritance, 'delete')}
          >
            <i className="fas fa-trash"></i>
          </button>
        );
        break;
      case 'delivered':
        buttons.push(
          <button 
            key="delete"
            className="admin-btn-icon admin-btn-danger" 
            title="Delete"
            onClick={() => handleInheritanceAction(inheritance, 'delete')}
          >
            <i className="fas fa-trash"></i>
          </button>
        );
        break;
    }

    return buttons;
  };

  return (
    <div className="admin-inheritances">
      <div className="admin-page-header">
        <h2>Inheritance Management</h2>
        <p>Manage digital inheritances and endowments</p>
      </div>

      <div className="admin-filter-bar">
        <div className="admin-search-filter">
          <input
            type="text"
            placeholder="Search inheritances..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <i className="fas fa-search"></i>
        </div>
        <div className="admin-action-filters">
          <select 
            value={typeFilter} 
            onChange={(e) => setTypeFilter(e.target.value)}
          >
            <option value="">Type: All</option>
            <option value="file">Files</option>
            <option value="password">Passwords</option>
            <option value="phrase">Phrases</option>
            <option value="banking">Banking</option>
          </select>
          <select 
            value={statusFilter} 
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="">Status: All</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="delivered">Delivered</option>
          </select>
        </div>
      </div>

      <div className="admin-table-container">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Inheritance Name</th>
              <th>Owner</th>
              <th>Receiver</th>
              <th>Type</th>
              <th>Status</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="7" className="admin-loading-data">
                  <p>Loading inheritance data...</p>
                </td>
              </tr>
            ) : inheritances.length === 0 ? (
              <tr>
                <td colSpan="7" className="admin-empty-data">
                  <p>No inheritances found. Try adjusting your filters.</p>
                </td>
              </tr>
            ) : (
              inheritances.map(inheritance => (
                <tr key={inheritance.id}>
                  <td>
                    <div className="admin-inheritance-name">
                      <strong>{inheritance.filename}</strong>
                    </div>
                  </td>
                  <td>{inheritance.creatorEmail}</td>
                  <td>{inheritance.recipientEmail || 'Not assigned'}</td>
                  <td>{getTypeBadge(inheritance.type)}</td>
                  <td>{getStatusBadge(inheritance.status)}</td>
                  <td>
                    {new Date(inheritance.createdAt).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric'
                    })}
                  </td>
                  <td>
                    <div className="admin-action-buttons">
                      {getActionButtons(inheritance)}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <AdminPagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
      />

      {/* Confirmation Dialog */}
      <AdminConfirmDialog
        show={confirmDialog.show}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmText={confirmDialog.confirmText}
        confirmClass={confirmDialog.confirmClass}
        onConfirm={confirmInheritanceAction}
        onCancel={() => setConfirmDialog({ show: false, inheritance: null, action: '' })}
      />
    </div>
  );
};

export default AdminInheritances;